import type { ComponentType, ReactNode } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { Button } from '@/components/ui/Button';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import type { StartupProfileData } from '@/features/profiles/api/startup-profile.api';
import { LogoSection } from '@/features/profiles/modules/startup/LogoSection';
import { CompanyOverviewSection } from '@/features/profiles/modules/startup/CompanyOverviewSection';
import { FinancialsSection } from '@/features/profiles/modules/startup/FinancialsSection';
import { PitchMaterialsSection } from '@/features/profiles/modules/startup/PitchMaterialsSection';
import { FoundingTeamSection } from '@/features/profiles/modules/startup/FoundingTeamSection';
import { CurrentRaiseSection } from '@/features/profiles/modules/startup/CurrentRaiseSection';
import { STARTUP_PROFILE_STRINGS } from '@/features/profiles/modules/startup/startup-profile.strings';
import type { CreateStartupFormValues } from '../../schema';
import { CREATE_STARTUP_STEPS, CREATE_STARTUP_STRINGS, SECTION_TITLES } from '../../constants';
import { useReviewProfileData } from '../../hooks/useReviewProfileData';
import { SaveProfileCard } from '../SaveProfileCard';

const S = UI_STRINGS.STARTUP_PROFILE;

type StepKey = (typeof CREATE_STARTUP_STEPS)[number]['key'];

interface ReviewSection {
  title: string;
  /** The wizard step that collects this section's fields - its Edit link's target. */
  step: StepKey;
  Section: ComponentType<{ mode: 'view'; data: StartupProfileData; headerRight?: ReactNode }>;
}

/** Same sections, same order, as the saved startup profile page. */
const REVIEW_SECTIONS: ReviewSection[] = [
  { title: STARTUP_PROFILE_STRINGS.COMPANY_LOGO, step: 'company', Section: LogoSection },
  { title: S.COMPANY_OVERVIEW, step: 'company', Section: CompanyOverviewSection },
  { title: S.FINANCIALS, step: 'company', Section: FinancialsSection },
  { title: S.PITCH_MATERIALS, step: 'company', Section: PitchMaterialsSection },
  { title: S.FOUNDING_TEAM, step: 'team', Section: FoundingTeamSection },
  { title: S.CURRENT_RAISE, step: 'raise', Section: CurrentRaiseSection },
];

function stepIndex(key: StepKey): number {
  return CREATE_STARTUP_STEPS.findIndex((step) => step.key === key);
}

interface StepProps {
  form: UseFormReturn<CreateStartupFormValues>;
  onEdit: (step: number) => void;
  isSaving: boolean;
}

/**
 * Step 4 "Review & Save": the founder's answers rendered with the saved
 * profile's own read-only sections (UX sheet r153), each with an Edit link
 * back to the step that collects it, then the save card.
 */
export function Step4Review({ form, onEdit, isSaving }: StepProps) {
  const data = useReviewProfileData(form.watch());

  return (
    <div className="flex w-full flex-col gap-6">
      <header>
        <h2 className="font-sans text-h5 text-navy">{SECTION_TITLES.REVIEW}</h2>
        <p className="text-sm text-subtext">{CREATE_STARTUP_STRINGS.REVIEW_INTRO}</p>
      </header>

      {REVIEW_SECTIONS.map(({ title, step, Section }) => (
        <Section
          key={title}
          mode="view"
          data={data}
          headerRight={
            <Button
              type="button"
              variant="link"
              aria-label={`${CREATE_STARTUP_STRINGS.EDIT} ${title}`}
              onClick={() => onEdit(stepIndex(step))}
            >
              {CREATE_STARTUP_STRINGS.EDIT}
            </Button>
          }
        />
      ))}

      <SaveProfileCard isSaving={isSaving} />
    </div>
  );
}
